import React, {useEffect, useRef, useState} from 'react';
import {Popover} from 'antd';

const AcneDetectionOverlay = ({acneAnalysis, originalImageUrl}) => {
    const [detectionData, setDetectionData] = useState(null);
    const [imageDimensions, setImageDimensions] = useState({});
    const [highlightedDetection, setHighlightedDetection] = useState(null);
    const imageRef = useRef(null);

    useEffect(() => {
        const fetchDetectionData = async () => {
            if (!acneAnalysis?.data_url) return;

            try {
                const response = await fetch(acneAnalysis.data_url);
                const data = await response.json();
                setDetectionData(data);
            } catch (error) {
                console.error('Error fetching acne detection data:', error);
            }
        };

        fetchDetectionData();
    }, [acneAnalysis]);

    useEffect(() => {
        const updateDimensions = () => {
            if (imageRef.current) {
                setImageDimensions({
                    width: imageRef.current.clientWidth,
                    height: imageRef.current.clientHeight,
                    originalWidth: imageRef.current.naturalWidth,
                    originalHeight: imageRef.current.naturalHeight
                });
            }
        };

        if (imageRef.current) {
            if (imageRef.current.complete) {
                updateDimensions();
            } else {
                imageRef.current.onload = updateDimensions;
            }

            const resizeObserver = new ResizeObserver(updateDimensions);
            resizeObserver.observe(imageRef.current);

            return () => {
                if (imageRef.current) {
                    resizeObserver.unobserve(imageRef.current);
                }
            };
        }
    }, [imageRef.current]);

    const getTagColor = (className) => {
        const colors = {
            'atrophic-hypertrophic_scar': 'orange',
            'melasma-nevus': '#5acd4b',
            'papule-pustule': '#bd47a1',
            'open-closed_comedo': '#73e5d5',
            'nodule-cyst': '#ff5722',
            'severe-acne-lesions': '#e91e63',
            'default': 'blue'
        };
        return colors[className] || colors.default;
    };

    const mapAcneDetail = (acneType) => {
        const acneDetails = {
            'open-closed_comedo': "Open/Closed Comedo",
            'papule-pustule': "Papule/Pustule",
            'nodule-cyst': "Nodule/Cyst",
            'severe-acne-lesions': "Severe Acne Lesions",
            'atrophic-hypertrophic_scar': "Atrophic/Hypertrophic Scar",
            'melasma-nevus': "Melasma/Nevus"
        };
        return acneDetails[acneType] || acneType;
    };

    const renderPopoverContent = (detection) => {
        return (
            <div style={{color: 'black'}}>
                <p><strong>Type:</strong> {mapAcneDetail(detection.class_name)}</p>
                <p><strong>Confidence:</strong> {(detection.score * 100).toFixed(1)}%</p>
                <p><strong>Location:</strong> [{detection.bbox[0].toFixed(0)}, {detection.bbox[1].toFixed(0)}]</p>
            </div>
        );
    };

    const renderDetections = () => {
        if (!detectionData || !detectionData.detections || !imageDimensions.width) {
            return null;
        }

        const detections = detectionData.detections;

        // Calculate scale based on object-fit: cover
        const scaleX = imageDimensions.width / imageDimensions.originalWidth;
        const scaleY = imageDimensions.height / imageDimensions.originalHeight;

        // object-fit: cover uses the larger scale to fill the container
        const scale = Math.max(scaleX, scaleY);

        // Calculate offsets for the cropped areas
        const scaledWidth = imageDimensions.originalWidth * scale;
        const scaledHeight = imageDimensions.originalHeight * scale;
        const offsetX = (scaledWidth - imageDimensions.width) / 2;
        const offsetY = (scaledHeight - imageDimensions.height) / 2;

        return detections.map((detection, index) => {
            // Scale the bounding box coordinates
            const x = detection.bbox[0] * scale - offsetX;
            const y = detection.bbox[1] * scale - offsetY;
            const width = (detection.bbox[2] - detection.bbox[0]) * scale;
            const height = (detection.bbox[3] - detection.bbox[1]) * scale;

            // Skip if detection is outside visible area
            if (x + width < 0 || y + height < 0 ||
                x > imageDimensions.width || y > imageDimensions.height) {
                return null;
            }

            const color = getTagColor(detection.class_name);
            const isHighlighted = highlightedDetection === index;

            // Calculate center and radius for circular marker
            const centerX = x + width / 2;
            const centerY = y + height / 2;
            const diagonal = Math.sqrt(width * width + height * height);
            const radius = diagonal / 2 + 5;

            // Clamp position and size to visible area
            const circleLeft = Math.max(0, centerX - radius);
            const circleTop = Math.max(0, centerY - radius);
            const circleWidth = Math.min(radius * 2, imageDimensions.width - circleLeft);
            const circleHeight = Math.min(radius * 2, imageDimensions.height - circleTop);

            return (
                <Popover
                    key={index}
                    content={renderPopoverContent(detection)}
                    title={`Detection #${index + 1}`}
                    trigger="hover"
                    placement="top"
                    open={isHighlighted}
                    overlayStyle={{zIndex: 1000}}
                >
                    <div
                        style={{
                            position: 'absolute',
                            left: `${circleLeft}px`,
                            top: `${circleTop}px`,
                            width: `${circleWidth}px`,
                            height: `${circleHeight}px`,
                            border: `2px solid ${color}`,
                            borderRadius: '50%',
                            backgroundColor: 'transparent',
                            cursor: 'pointer',
                            zIndex: isHighlighted ? 9 : 4,
                            transition: 'all 0.3s ease',
                            boxShadow: isHighlighted ? `0 0 12px ${color}` : 'none'
                        }}
                        onMouseEnter={() => setHighlightedDetection(index)}
                        onMouseLeave={() => setHighlightedDetection(null)}
                        onClick={() => setHighlightedDetection(isHighlighted ? null : index)}
                    />
                </Popover>
            );
        });
    };

    return (
        <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%'
        }}>
            <img
                ref={imageRef}
                src={originalImageUrl}
                alt="Original with detections"
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                }}
            />
            {renderDetections()}
        </div>
    );
};

export default AcneDetectionOverlay;

